import { useUser } from "@clerk/clerk-react";
import type React from "react";
import { createContext, useCallback, useContext } from "react";

export interface CurrentUser {
	id: string;
	email: string | null;
	role: "owner" | "member" | null;
	organisationId: string | null;
}

interface CurrentUserContextValue {
	currentUser: CurrentUser | null;
	isLoading: boolean;
	refetch: () => void;
}

const CurrentUserContext = createContext<CurrentUserContextValue>({
	currentUser: null,
	isLoading: true,
	refetch: () => {},
});

export const CurrentUserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const { user, isLoaded, isSignedIn } = useUser();

	const currentUser: CurrentUser | null =
		isLoaded && isSignedIn && user
			? {
					id: user.id,
					email: user.primaryEmailAddress?.emailAddress ?? null,
					role: (user.publicMetadata?.role as "owner" | "member") ?? null,
					organisationId: (user.publicMetadata?.organisationId as string) ?? null,
				}
			: null;

	const refetch = useCallback(async () => {
		await user?.reload();
	}, [user]);

	return (
		<CurrentUserContext.Provider value={{ currentUser, isLoading: !isLoaded, refetch }}>
			{children}
		</CurrentUserContext.Provider>
	);
};

export const useCurrentUser = () => useContext(CurrentUserContext);
